import { useState, useCallback, useEffect, useRef } from 'react'
import { twitchApi } from '../services/twitchApi'
import { commandHandler } from '../services/commandHandler'

export function useTwitchConnection(broadcasterLogin) {
  const [status, setStatus] = useState('Initializing...')
  const [isConnected, setIsConnected] = useState(false)
  const [needsAuth, setNeedsAuth] = useState(false)
  const [authUrl, setAuthUrl] = useState(null)
  const messageHandlerRegistered = useRef(false)

  useEffect(() => {
    // Set broadcaster context when it changes
    if (broadcasterLogin) {
      twitchApi.setBroadcasterLogin(broadcasterLogin)
    }

    // Subscribe to status updates
    const unsubscribe = twitchApi.onStatus(setStatus)

    // Listen for auth success from popup
    const handleAuthMessage = (event) => {
      if (event.data?.type === 'TWITCH_AUTH_SUCCESS') {
        // If the auth was for a different broadcaster, redirect
        if (event.data.login && event.data.login !== broadcasterLogin) {
          window.location.href = `/overlay/${event.data.login}`
          return
        }
        setNeedsAuth(false)
        connect()
      }
    }
    window.addEventListener('message', handleAuthMessage)

    return () => {
      unsubscribe()
      window.removeEventListener('message', handleAuthMessage)
    }
  }, [broadcasterLogin])

  const connect = useCallback(async () => {
    if (!broadcasterLogin) {
      setStatus('No broadcaster specified')
      return
    }

    try {
      // Register message handler (only once)
      if (!messageHandlerRegistered.current) {
        twitchApi.onMessage((message) => {
          commandHandler.processMessage(message)
        })
        messageHandlerRegistered.current = true
      }

      // Connect to Twitch
      const result = await twitchApi.connect()

      if (result?.needsAuth) {
        setNeedsAuth(true)
        setAuthUrl(result.authUrl)
        setIsConnected(false)
      } else {
        setNeedsAuth(false)
        setIsConnected(true)
      }
    } catch (error) {
      console.error('Failed to connect:', error)
      setStatus('Connection failed')
      setIsConnected(false)
    }
  }, [broadcasterLogin])

  const disconnect = useCallback(() => {
    twitchApi.disconnect()
    setIsConnected(false)
    setStatus('Disconnected')
  }, [])

  const openAuthPopup = useCallback(() => {
    if (authUrl) {
      window.open(authUrl, 'TwitchAuth', 'width=500,height=700')
    }
  }, [authUrl])

  return {
    status,
    isConnected,
    needsAuth,
    authUrl,
    connect,
    disconnect,
    openAuthPopup
  }
}
